import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import Navbar from "../../components/Navbar";
import "./OnboardingPage.css";
import Login from "../../components/Login";

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const { showError, showSuccess, showWarning } = useToast();

  // Bulk user creation states
  const [showBulkUserForm, setShowBulkUserForm] = useState(false);
  const [bulkUsers, setBulkUsers] = useState([
    {
      full_name: "",
      email: "",
      address: "",
      whatsapp_number: "",
      job_title: "",
      company: "",
      topic: ""
    }
  ]);
  const [bulkUserLoading, setBulkUserLoading] = useState(false);

  useEffect(() => {
    // no-op: keep for potential side-effects when auth/loading changes
  }, [loading, isAuthenticated]);

  // Bulk user creation handlers
  const handleBulkUserInputChange = (index, field, value) => {
    const updatedUsers = [...bulkUsers];
    updatedUsers[index][field] = value;
    setBulkUsers(updatedUsers);
  };

  const addNewUser = () => {
    setBulkUsers([
      ...bulkUsers,
      {
        full_name: "",
        email: "",
        address: "",
        whatsapp_number: "",
        job_title: "",
        company: "",
        topic: ""
      }
    ]);
  };

  const removeUser = (index) => {
    if (bulkUsers.length > 1) {
      const updatedUsers = bulkUsers.filter((_, i) => i !== index);
      setBulkUsers(updatedUsers);
    }
  };

  const handleBulkUserSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that all required fields are filled
    const isValid = bulkUsers.every(user => 
      user.full_name && user.email && user.address && 
      user.whatsapp_number && user.job_title && user.company && user.topic
    );

    if (!isValid) {
      showWarning("Please fill in all fields for all users");
      return;
    }

    // Validate email format
    const emailValid = bulkUsers.every(user => user.email.includes('@'));
    if (!emailValid) {
      showWarning("Please enter valid email addresses for all users");
      return;
    }

    setBulkUserLoading(true);

    try {
      const response = await fetch('https://pulse.upstartaec.com/agent/survey-generation/create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ users: bulkUsers }),
      });

      const data = await response.json();

      if (data.success) {
        showSuccess(data.message || "Users created successfully!");
        setBulkUsers([{
          full_name: "",
          email: "",
          address: "",
          whatsapp_number: "",
          job_title: "",
          company: "",
          topic: ""
        }]);
        setShowBulkUserForm(false);
      } else {
        throw new Error(data.error || "Failed to create users");
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to create users. Please try again.";
      showError(errorMessage);
    } finally {
      setBulkUserLoading(false);
    }
  };

  // Note: login handled by dedicated Login component; onboarding only shows features when authenticated

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
            <div className="absolute inset-0 w-20 h-20 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-full animate-ping opacity-20"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Loading Onboarding
          </h2>
          <p className="text-gray-600 text-lg">Please wait...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Login/>
    );
  }

  // If authenticated, show onboarding content
  return (
    <>
      <Navbar />
      <div className="onboarding-container">
        <div className="onboarding-header">
          <h1>Welcome to SurveyFlow Pro!</h1>
          <p>Your comprehensive survey management platform</p>
        </div>

        <div className="features-grid">
          <div className="feature-card" onClick={() => navigate("/dashboard")}>
            <div className="feature-icon">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3>Analytics Dashboard</h3>
            <p>Get insights into your survey performance with comprehensive analytics</p>
            <div className="feature-link">
              Go to Dashboard
            </div>
          </div>

          <div className="feature-card" onClick={() => navigate("/hired-candidates")}>
            <div className="feature-icon">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3>Talent Management</h3>
            <p>Manage hired talents and track their survey responses</p>
            <div className="feature-link">
              Manage Talents
            </div>
          </div>

          <div className="feature-card" onClick={() => setShowBulkUserForm(true)}>
            <div className="feature-icon">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4m0 0l4-4m-4 4l4 4" />
              </svg>
            </div>
            <h3>Bulk User Creation</h3>
            <p>Add multiple hired talents to the system at once</p>
            <div className="feature-link">
              Create Users
            </div>
          </div>
        </div>

        {/* Bulk User Creation Modal */}
        {showBulkUserForm && (
          <div className="modal-overlay" onClick={() => setShowBulkUserForm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">Bulk User Creation</h2>
                <button 
                  className="modal-close"
                  onClick={() => setShowBulkUserForm(false)}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleBulkUserSubmit} className="bulk-user-form">
                {bulkUsers.map((user, index) => (
                  <div key={index} className="user-form-section">
                    <div className="user-form-header">
                      <h3 className="user-form-title">User {index + 1}</h3>
                      {bulkUsers.length > 1 && (
                        <button
                          type="button"
                          className="remove-user-btn"
                          onClick={() => removeUser(index)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Full Name *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={user.full_name}
                          onChange={(e) => handleBulkUserInputChange(index, 'full_name', e.target.value)}
                          placeholder="Enter full name"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email *</label>
                        <input
                          type="email"
                          className="form-input"
                          value={user.email}
                          onChange={(e) => handleBulkUserInputChange(index, 'email', e.target.value)}
                          placeholder="Enter email address"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Address *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={user.address}
                          onChange={(e) => handleBulkUserInputChange(index, 'address', e.target.value)}
                          placeholder="Enter address"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">WhatsApp Number *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={user.whatsapp_number}
                          onChange={(e) => handleBulkUserInputChange(index, 'whatsapp_number', e.target.value)}
                          placeholder="Enter WhatsApp number"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Job Title *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={user.job_title}
                          onChange={(e) => handleBulkUserInputChange(index, 'job_title', e.target.value)}
                          placeholder="Enter job title"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Company *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={user.company}
                          onChange={(e) => handleBulkUserInputChange(index, 'company', e.target.value)}
                          placeholder="Enter company name"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Topic *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={user.topic}
                        onChange={(e) => handleBulkUserInputChange(index, 'topic', e.target.value)}
                        placeholder="Enter topic"
                        required
                      />
                    </div>
                  </div>
                ))}

                <div className="form-actions">
                  <button
                    type="button"
                    className="add-user-btn"
                    onClick={addNewUser}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Another User
                  </button>

                  <div className="submit-actions">
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => setShowBulkUserForm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={bulkUserLoading}
                    >
                      {bulkUserLoading ? (
                        <>
                          <div className="spinner"></div>
                          <span>Creating Users...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Create Users</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default OnboardingPage;
